import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { User, Heart, Fire, Trophy, ThumbsUp, MessageCircle, Send, X, Image, Video } from "../components/common/Icons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Post Card Component
const PostCard = ({ post, currentUser, isLoggedIn, onReact, onComment, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const reactions = [
    { type: 'like', icon: <ThumbsUp />, label: 'Me gusta', color: 'text-blue-400' },
    { type: 'love', icon: <Heart filled />, label: 'Me encanta', color: 'text-red-400' },
    { type: 'fire', icon: <Fire />, label: 'Fuego', color: 'text-orange-400' },
    { type: 'trophy', icon: <Trophy size={18} />, label: 'Trofeo', color: 'text-yellow-400' },
  ];

  const totalReactions = Object.values(post.reactions || {}).reduce((acc, arr) => acc + arr.length, 0);
  const currentUserId = currentUser?.user_id;
  const isAdmin = currentUser?.role === 'admin';
  const canDelete = currentUserId === post.user_id || isAdmin;

  const loadComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      try {
        const res = await axios.get(`${API}/posts/${post.id}/comments`);
        setComments(res.data);
      } catch (e) {
        console.error('Error loading comments:', e);
      }
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !isLoggedIn) return;
    try {
      const commentData = {
        post_id: post.id,
        user_id: currentUserId,
        user_name: currentUser?.name || 'Usuario',
        content: newComment
      };
      const res = await axios.post(`${API}/comments`, commentData, { withCredentials: true });
      setComments([...comments, res.data]);
      setNewComment('');
      onComment(post.id);
    } catch (e) {
      console.error('Error adding comment:', e);
    }
  };

  const getUserReaction = () => {
    if (!currentUserId) return null;
    for (const [type, users] of Object.entries(post.reactions || {})) {
      if (users.includes(currentUserId)) return type;
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden" data-testid={`post-${post.id}`}>
      {/* Post Header */}
      <div className="p-3 md:p-4 flex items-center gap-3">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden flex-shrink-0">
          {post.user_avatar ? (
            <img src={post.user_avatar} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold text-sm">{post.user_name?.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm md:text-base truncate">{post.user_name}</p>
          <p className="text-xs md:text-sm text-gray-400">{formatTimestamp(post.created_at)}</p>
        </div>
        {canDelete && (
          <button 
            onClick={() => onDelete(post.id)} 
            className="text-gray-500 hover:text-red-400 transition-colors"
            title={isAdmin && post.user_id !== currentUserId ? "Eliminar como admin" : "Eliminar"}
          >
            <X />
          </button>
        )}
      </div>

      {/* Post Content */}
      <div className="px-3 md:px-4 pb-3 md:pb-4">
        <p className="text-gray-200 whitespace-pre-wrap text-sm md:text-base">{post.content}</p>
      </div>

      {/* Post Media */}
      {post.media_urls?.length > 0 && (
        <div className={`grid gap-1 ${post.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {post.media_urls.map((url, index) => (
            <div key={index} className="aspect-video bg-gray-800 overflow-hidden">
              {post.media_types?.[index] === 'video' ? (
                <video src={`${BACKEND_URL}${url}`} controls className="w-full h-full object-cover" />
              ) : (
                <img src={`${BACKEND_URL}${url}`} alt="Media" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reactions Count */}
      {(totalReactions > 0 || post.comment_count > 0) && (
        <div className="px-3 md:px-4 py-2 flex items-center justify-between text-xs md:text-sm text-gray-400 border-t border-purple-800/30">
          <span>{totalReactions > 0 && `${totalReactions} reacciones`}</span>
          <span onClick={loadComments} className="cursor-pointer hover:text-pink-400">
            {post.comment_count > 0 && `${post.comment_count} comentarios`}
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-2 md:px-4 py-2 flex items-center justify-around border-t border-purple-800/30">
        {reactions.map(({ type, icon, label, color }) => {
          const isActive = getUserReaction() === type;
          return (
            <button
              key={type}
              onClick={() => onReact(post.id, type)}
              className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors ${isActive ? `${color} bg-white/10` : 'text-gray-400 hover:bg-white/5'}`}
              title={label}
            >
              {icon}
              <span className="hidden md:inline text-xs md:text-sm">{label}</span>
            </button>
          );
        })}
        <button
          onClick={loadComments}
          className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-gray-400 hover:bg-white/5 transition-colors"
        >
          <MessageCircle />
          <span className="hidden md:inline text-xs md:text-sm">Comentar</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-purple-800/30 p-3 md:p-4">
          {loadingComments ? (
            <div className="text-center text-gray-400 py-4 text-sm">Cargando comentarios...</div>
          ) : (
            <>
              <div className="space-y-2 md:space-y-3 max-h-48 md:max-h-60 overflow-y-auto mb-3 md:mb-4">
                {comments.map(comment => (
                  <div key={comment.id} className="flex gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{comment.user_name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 bg-gray-800/50 rounded-xl px-3 py-2">
                      <p className="font-semibold text-white text-xs md:text-sm">{comment.user_name}</p>
                      <p className="text-gray-300 text-xs md:text-sm">{comment.content}</p>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-gray-500 text-center text-xs md:text-sm">No hay comentarios aún. ¡Sé el primero!</p>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escribe un comentario..."
                  className="flex-1 px-3 md:px-4 py-2 bg-gray-800 border border-purple-700 rounded-full text-white text-xs md:text-sm placeholder:text-gray-500 focus:outline-none focus:border-pink-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button
                  onClick={handleAddComment}
                  className="p-2 bg-pink-500 rounded-full text-white hover:bg-pink-600 transition-colors"
                >
                  <Send />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Create Post Form
const CreatePostForm = ({ user, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + mediaFiles.length > 4) {
      alert('Máximo 4 archivos permitidos');
      return;
    }

    const newFiles = [...mediaFiles, ...files].slice(0, 4);
    setMediaFiles(newFiles);

    const previews = newFiles.map(file => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video') ? 'video' : 'image'
    }));
    setMediaPreviews(previews);
  };

  const removeMedia = (index) => {
    const newFiles = mediaFiles.filter((_, i) => i !== index);
    const newPreviews = mediaPreviews.filter((_, i) => i !== index);
    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);
  };

  const handleSubmit = async () => {
    if (!content.trim() && mediaFiles.length === 0) return;

    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      // Upload media files first
      const uploadedMedia = [];
      for (let i = 0; i < mediaFiles.length; i++) {
        const file = mediaFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, { withCredentials: true });
        uploadedMedia.push(res.data);
        setUploadProgress(((i + 1) / mediaFiles.length) * 50);
      }

      // Create post with authentication
      const postData = {
        content: content,
        media_urls: uploadedMedia.map(m => m.url),
        media_types: uploadedMedia.map(m => m.media_type)
      };

      setUploadProgress(75);
      const res = await axios.post(`${API}/posts`, postData, { withCredentials: true });
      setUploadProgress(100);
      onPostCreated(res.data);
      setContent('');
      setMediaFiles([]);
      setMediaPreviews([]);
    } catch (e) {
      console.error('Error creating post:', e);
      alert('Error al crear la publicación');
    }
    setIsSubmitting(false);
    setUploadProgress(0);
  };

  return (
    <div className="glass-card rounded-xl p-3 md:p-4" data-testid="create-post-form">
      <div className="flex gap-3">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden flex-shrink-0">
          {user?.picture ? (
            <img src={user.picture} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold text-sm">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
          )}
        </div>
        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="¿Qué estás pensando? Comparte con la comunidad..."
            className="w-full bg-transparent border-none text-white placeholder:text-gray-500 resize-none focus:outline-none min-h-[60px] md:min-h-[80px] text-sm md:text-base"
            rows={3}
          />

          {/* Media Previews */}
          {mediaPreviews.length > 0 && (
            <div className={`grid gap-2 mt-3 ${mediaPreviews.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative aspect-video bg-gray-800 rounded-lg overflow-hidden">
                  {preview.type === 'video' ? (
                    <video src={preview.url} className="w-full h-full object-cover" />
                  ) : (
                    <img src={preview.url} alt="Preview" className="w-full h-full object-cover" />
                  )}
                  <button
                    onClick={() => removeMedia(index)}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  >
                    <X />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="mt-3">
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-pink-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">Subiendo... {Math.round(uploadProgress)}%</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 md:mt-4 pt-3 md:pt-4 border-t border-purple-800/30">
            <div className="flex gap-1 md:gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*"
                multiple
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors text-xs md:text-sm"
                disabled={mediaFiles.length >= 4}
              >
                <Image /> <span className="hidden md:inline">Foto</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors text-xs md:text-sm"
                disabled={mediaFiles.length >= 4}
              >
                <Video /> <span className="hidden md:inline">Video</span>
              </button>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || (!content.trim() && mediaFiles.length === 0)}
              className="px-4 md:px-6 py-1.5 md:py-2 bg-gradient-to-r from-pink-500 to-red-500 rounded-full font-semibold text-white text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-pink-500/30 transition-all"
            >
              {isSubmitting ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Community Page
const CommunityPage = ({ isLoggedIn, user }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const observerRef = useRef();

  const loadPosts = async (reset = false) => {
    const newSkip = reset ? 0 : skip;
    try {
      const res = await axios.get(`${API}/posts?skip=${newSkip}&limit=10`);
      if (reset) {
        setPosts(res.data);
      } else {
        setPosts(prev => [...prev, ...res.data]);
      }
      setHasMore(res.data.length === 10);
      setSkip(newSkip + 10);
    } catch (e) {
      console.error('Error loading posts:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPosts(true);
  }, []);

  // Infinite scroll observer
  const lastPostRef = useCallback(node => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadPosts();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore]);

  const handleReact = async (postId, reactionType) => {
    if (!isLoggedIn) {
      alert('Debes iniciar sesión para reaccionar');
      return;
    }
    try {
      const res = await axios.post(`${API}/posts/${postId}/react`, {
        user_id: user.user_id,
        reaction_type: reactionType
      }, { withCredentials: true });
      setPosts(posts.map(p => p.id === postId ? { ...p, reactions: res.data.reactions } : p));
    } catch (e) {
      console.error('Error reacting:', e);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const handleComment = (postId) => {
    setPosts(posts.map(p => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta publicación?')) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setPosts(posts.filter(p => p.id !== postId));
    } catch (e) {
      console.error('Error deleting post:', e);
      if (e.response?.status === 403) {
        alert('No tienes permiso para eliminar esta publicación');
      } else if (e.response?.status === 401) {
        alert('Debes iniciar sesión para eliminar publicaciones');
      } else {
        alert('Error al eliminar la publicación');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="community-page">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-black">
            <span className="bg-gradient-to-r from-red-500 via-pink-500 to-yellow-400 bg-clip-text text-transparent">
              Comunidad Nirvana
            </span>
          </h1>
          <p className="text-gray-400 mt-2 text-sm md:text-base">Comparte, comenta y conecta con otros duelistas</p>
        </div>

        {/* Create Post */}
        {isLoggedIn ? (
          <div className="mb-6 md:mb-8">
            <CreatePostForm 
              user={user}
              onPostCreated={handlePostCreated}
            />
          </div>
        ) : (
          <div className="glass-card rounded-xl p-4 md:p-6 text-center mb-6 md:mb-8">
            <p className="text-gray-400 text-sm md:text-base">Inicia sesión para crear publicaciones y compartir con la comunidad</p>
          </div>
        )}

        {/* Posts Feed */}
        <div className="space-y-4 md:space-y-6">
          {loading && posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-4 text-sm md:text-base">Cargando publicaciones...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-sm md:text-base">No hay publicaciones aún. ¡Sé el primero en publicar!</p>
            </div>
          ) : (
            posts.map((post, index) => (
              <div key={post.id} ref={index === posts.length - 1 ? lastPostRef : null}>
                <PostCard
                  post={post}
                  currentUser={user}
                  isLoggedIn={isLoggedIn}
                  onReact={handleReact}
                  onComment={handleComment}
                  onDelete={handleDelete}
                />
              </div>
            ))
          )}
          
          {loading && posts.length > 0 && (
            <div className="text-center py-4">
              <div className="inline-block w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
